import { useRef, useState } from "react";
import { Nav } from "react-bootstrap";
import { classMerge } from "../utility";
import styles from "./ReorderableNavList.module.css";

/**
 * Minimum shape required for an item rendered by ReorderableNavList.
 */
export interface ReorderableNavListItem {
  /** Unique identifier, used as the React key and drag payload */
  id: string;
  /** Display name shown on the tab */
  name: string;
}

/**
 * Props for the ReorderableNavList component.
 */
export interface ReorderableNavListProps<T extends ReorderableNavListItem> {
  /** The ordered list of items rendered as navigation tabs */
  list: T[];
  /** Called with the reordered list when a tab has been dropped in a new position */
  setList: (ret: T[]) => void;
  /** Optional function returning the Nav.Link eventKey for an item, defaults to the item index */
  getEventKey?: (item: T, index: number) => string;
  /** Optional flag preventing reordering and disabling the tabs */
  disabled?: boolean;
  /** Optional flag preventing reordering while leaving the tabs enabled */
  disableReorder?: boolean;
}

/**
 * Renders a list of items as draggable Bootstrap navigation tabs.
 *
 * While a tab is dragged the browser shows a copy of that tab as the drag image,
 * and the tabs it would displace slide sideways by the dragged tab's width, so the
 * resulting order is visible before the drop. Dropping calls `setList` with a new
 * array in the updated order; persisting that order is the caller's responsibility.
 *
 * @param props - Component props.
 * @param props.list - The ordered list of items rendered as navigation tabs.
 * @param props.setList - Called with the reordered list once a tab is dropped in a new position.
 * @param props.getEventKey - Optional function returning the Nav.Link eventKey for an item.
 * @param props.disabled - Optional flag preventing reordering and disabling the tabs.
 * @param props.disableReorder - Optional flag preventing reordering while leaving the tabs enabled.
 * @returns Navigation items which can be reordered by dragging.
 */
export const ReorderableNavList = <T extends ReorderableNavListItem>({
  list,
  setList,
  getEventKey,
  disabled = false,
  disableReorder = false,
}: ReorderableNavListProps<T>): React.ReactElement => {
  /** Whether tabs may be picked up, as either flag blocks reordering */
  const canReorder = !disabled && !disableReorder;
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [dragWidth, setDragWidth] = useState(0);
  /** Horizontal midpoints of each tab, captured before any drag transforms are applied */
  const midPoints = useRef<number[]>([]);
  /** Latest calculated drop index, read on drop because state updates lag drag events */
  const targetIndex = useRef<number | null>(null);
  /** Extent of the tab strip, used to ignore drops released outside it */
  const listBounds = useRef<{ left: number; right: number; top: number; bottom: number }>({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  });
  /** The tab strip element, listened to during a drag so gaps between tabs accept the drop */
  const container = useRef<HTMLElement | null>(null);
  /** Index of the tab being dragged, mirrored in a ref for the native strip listeners */
  const activeDragIndex = useRef<number | null>(null);
  /** Whether the pointer was last seen over the tab strip, recorded during the drag */
  const overList = useRef(false);

  /**
   * Marks the whole tab strip as a valid drop zone. Without this the gaps between
   * tabs reject the drag and the browser shows the "not allowed" cursor even though
   * releasing there would still reorder the list.
   *
   * @param event - The native drag event raised anywhere over the tab strip.
   */
  const allowDrop = (event: DragEvent): void => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
    /* istanbul ignore else -- the strip only listens while a drag is in progress */
    if (activeDragIndex.current !== null) {
      const nextIndex = getTargetIndex(event.clientX, activeDragIndex.current);
      targetIndex.current = nextIndex;
      setOverIndex(nextIndex);
    }
  };

  /**
   * Records whether the pointer is still over the tab strip. Drag end reports
   * unreliable coordinates, so the last known position decides whether the move is
   * committed, and this runs on the document so positions outside the strip count.
   *
   * @param event - The native drag event raised anywhere in the document.
   */
  const trackPointer = (event: DragEvent): void => {
    overList.current =
      event.clientX >= listBounds.current.left &&
      event.clientX <= listBounds.current.right &&
      event.clientY >= listBounds.current.top &&
      event.clientY <= listBounds.current.bottom;
  };

  /**
   * Stops the tab strip accepting drops once the drag is over.
   */
  const releaseContainer = (): void => {
    container.current?.removeEventListener("dragover", allowDrop);
    container.current?.removeEventListener("drop", allowDrop);
    document.removeEventListener("dragover", trackPointer);
    container.current = null;
  };

  /**
   * Moves the dragged item to the target position and notifies the caller.
   *
   * @param from - Index of the item being dragged.
   * @param to - Index the item should be moved to.
   */
  const moveItem = (from: number, to: number): void => {
    if (from !== to) {
      const newList = [...list];
      const [moved] = newList.splice(from, 1);
      newList.splice(to, 0, moved);
      setList(newList);
    }
  };

  /**
   * Clears all drag tracking state once a drag has finished or been cancelled.
   */
  const endDrag = (): void => {
    setDragIndex(null);
    setOverIndex(null);
    setDragWidth(0);
    midPoints.current = [];
    listBounds.current = { left: 0, right: 0, top: 0, bottom: 0 };
    targetIndex.current = null;
    overList.current = false;
    releaseContainer();
  };

  /**
   * Commits the pending move when the drag finishes. This runs on drag end rather
   * than only on drop because the sliding tabs can leave a gap beneath the pointer,
   * in which case no drop event is raised by any tab.
   */
  const commitDrag = (): void => {
    if (dragIndex !== null && targetIndex.current !== null && overList.current) {
      moveItem(dragIndex, targetIndex.current);
    }
    activeDragIndex.current = null;
    endDrag();
  };

  /**
   * Records the untransformed midpoint of every tab so that the drop position can
   * be calculated from the pointer while the tabs are being slid out of the way.
   *
   * @param source - The tab wrapper the drag started from.
   */
  const captureLayout = (source: HTMLDivElement): void => {
    // The tabs are always rendered as div > Nav.Item > Nav, so the strip is present
    const strip = source.parentElement!.parentElement!;
    const wrappers = Array.from(strip.querySelectorAll<HTMLDivElement>("[data-reorderable-index]"));
    midPoints.current = wrappers.map((wrapper) => {
      const rect = wrapper.getBoundingClientRect();
      return rect.left + rect.width / 2;
    });
    const stripRect = strip.getBoundingClientRect();
    listBounds.current = {
      left: stripRect.left,
      right: stripRect.right,
      top: stripRect.top,
      bottom: stripRect.bottom,
    };
    container.current = strip;
    strip.addEventListener("dragover", allowDrop);
    strip.addEventListener("drop", allowDrop);
    document.addEventListener("dragover", trackPointer);
  };

  /**
   * Works out which position the dragged tab would take if dropped at the pointer.
   *
   * @param clientX - Horizontal pointer position of the drag event.
   * @param fallback - Index used when the layout could not be captured.
   * @returns The index the dragged tab should move to.
   */
  const getTargetIndex = (clientX: number, fallback: number): number => {
    let target = fallback;
    /* istanbul ignore else -- the layout is always captured before a drag reports a position */
    if (midPoints.current.length === list.length) {
      target = midPoints.current.filter((midPoint) => midPoint < clientX).length;
      target = Math.min(target, list.length - 1);
    }
    return target;
  };

  /**
   * Uses a detached copy of the dragged tab as the drag image, so the item
   * following the cursor looks identical to the tab that was picked up.
   *
   * @param event - The drag start event raised by the tab wrapper.
   */
  const setDragImageFromTab = (event: React.DragEvent<HTMLDivElement>): void => {
    const source = event.currentTarget;
    const preview = source.cloneNode(true) as HTMLDivElement;
    preview.style.width = `${source.offsetWidth}px`;
    preview.classList.add(styles.reorderableNavDragImage);
    // The clone is decorative only, so it must not be discoverable as content
    preview.setAttribute("aria-hidden", "true");
    preview.removeAttribute("data-reorderable-index");
    document.body.appendChild(preview);
    const rect = source.getBoundingClientRect();
    event.dataTransfer.setDragImage(preview, event.clientX - rect.left, event.clientY - rect.top);
    // The preview must remain in the document until the browser has snapshotted it
    setTimeout(() => preview.remove(), 0);
  };

  /**
   * Determines how far a tab should slide to show where the dragged tab will land.
   *
   * @param index - Index of the tab being rendered.
   * @returns The CSS module class applying the required shift, or an empty string.
   */
  const getShiftClass = (index: number): string => {
    let shiftClass = "";
    if (dragIndex !== null && overIndex !== null) {
      if (index === dragIndex) {
        shiftClass = styles.reorderableNavItemDragging;
      } else if (index > dragIndex && index <= overIndex) {
        shiftClass = styles.reorderableNavItemShiftStart;
      } else if (index < dragIndex && index >= overIndex) {
        shiftClass = styles.reorderableNavItemShiftEnd;
      }
    }
    return shiftClass;
  };

  return (
    <>
      {list.map((item, index) => (
        <Nav.Item key={item.id}>
          <div
            draggable={canReorder}
            onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
              if (canReorder) {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", item.id);
                activeDragIndex.current = index;
                targetIndex.current = index;
                captureLayout(e.currentTarget);
                setDragImageFromTab(e);
                setDragWidth(e.currentTarget.offsetWidth);
                setDragIndex(index);
              }
            }}
            onDragEnd={commitDrag}
            className={classMerge([
              styles.reorderableNavItem,
              dragIndex === null ? styles.reorderableNavItemStatic : "",
              canReorder ? styles.reorderableNavItemDraggable : "",
              getShiftClass(index),
            ])}
            style={{ "--mos-reorderable-drag-width": `${dragWidth}px` } as React.CSSProperties}
            data-reorderable-index={index}
          >
            <Nav.Link
              eventKey={getEventKey?.(item, index) ?? `${index}`}
              disabled={disabled}
            >
              {item.name}
            </Nav.Link>
          </div>
        </Nav.Item>
      ))}
    </>
  );
};

ReorderableNavList.displayName = "ReorderableNavList";
